import { useEffect, useState } from "react";
import api, { formatIDR, formatDate } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Plus, Trash2, ChevronRight, ChevronLeft } from "lucide-react";
import { toast } from "sonner";
import AttachmentBox from "@/components/AttachmentBox";

const statusMap = {
  planning: { label: "Perencanaan", color: "bg-forest-400" },
  active: { label: "Aktif", color: "bg-[#4A6B53]" },
  "on-hold": { label: "Ditahan", color: "bg-[#D99C3B]" },
  completed: { label: "Selesai", color: "bg-forest-600" },
};

const taskStatusMap = { todo: "To Do", "in-progress": "In Progress", done: "Done" };

export default function Projects() {
  const [projects, setProjects] = useState([]);
  const [clients, setClients] = useState([]);
  const [selected, setSelected] = useState(null);
  const [tasks, setTasks] = useState([]);
  const [openP, setOpenP] = useState(false);
  const [openT, setOpenT] = useState(false);
  const emptyP = { name: "", client_id: "", description: "", start_date: "", end_date: "", budget: 0, status: "planning" };
  const emptyT = { project_id: "", title: "", description: "", assignee: "", due_date: "", priority: "medium", status: "todo" };
  const [pForm, setPForm] = useState(emptyP);
  const [tForm, setTForm] = useState(emptyT);

  const load = () => api.get("/projects").then(r=>{setProjects(r.data); if (!selected && r.data[0]) setSelected(r.data[0]);});
  const loadTasks = (pid) => api.get(`/tasks?project_id=${pid}`).then(r=>setTasks(r.data));
  useEffect(()=>{load(); api.get("/parties?kind=client").then(r=>setClients(r.data));},[]);
  useEffect(()=>{ if(selected) loadTasks(selected.id); },[selected]);

  const saveProject = async () => {
    if (!pForm.name) return toast.error("Nama project wajib");
    try {
      const body = {...pForm, budget:+pForm.budget};
      const { data } = await api.post("/projects", body); toast.success("Project dibuat"); setOpenP(false); setPForm(emptyP);
      const r = await api.get("/projects"); setProjects(r.data); setSelected(data);
    } catch { toast.error("Gagal"); }
  };
  const removeProject = async (id) => {
    if(!confirm("Hapus project?")) return;
    await api.delete(`/projects/${id}`); if(selected?.id===id) setSelected(null); load();
  };
  const saveTask = async () => {
    if (!selected) return;
    try { await api.post("/tasks", {...tForm, project_id: selected.id}); setOpenT(false); setTForm(emptyT); loadTasks(selected.id); }
    catch { toast.error("Gagal"); }
  };
  const moveTask = async (task, dir) => {
    const order = ["todo","in-progress","done"];
    const idx = order.indexOf(task.status);
    const next = order[Math.min(order.length-1, Math.max(0, idx+dir))];
    await api.put(`/tasks/${task.id}`, {...task, status: next});
    loadTasks(selected.id);
  };
  const delTask = async (id) => { await api.delete(`/tasks/${id}`); loadTasks(selected.id); };

  const columns = ["todo","in-progress","done"];

  return (
    <div className="space-y-6">
      <div className="flex items-end justify-between">
        <div>
          <div className="label-tag">Project Hub</div>
          <h1 className="font-display text-4xl tracking-tight mt-1">Manajemen Project</h1>
          <p className="text-sm text-forest-400 mt-2">Kelola project dan tugas tim Anda dengan board kanban.</p>
        </div>
        <Dialog open={openP} onOpenChange={(v)=>{setOpenP(v); if(!v)setPForm(emptyP);}}>
          <DialogTrigger asChild>
            <Button data-testid="btn-add-project" className="rounded-sm bg-[#2D4739] hover:bg-[#22362B] gap-2"><Plus size={16}/> Project Baru</Button>
          </DialogTrigger>
          <DialogContent className="bg-[#FDFDFB] rounded-sm max-w-xl">
            <DialogHeader><DialogTitle className="font-display">Project Baru</DialogTitle></DialogHeader>
            <div className="grid grid-cols-2 gap-3">
              <div className="col-span-2"><Label>Nama Project</Label><Input data-testid="inp-project-name" value={pForm.name} onChange={e=>setPForm({...pForm, name:e.target.value})}/></div>
              <div className="col-span-2"><Label>Klien</Label>
                <Select value={pForm.client_id||""} onValueChange={(v)=>setPForm({...pForm, client_id:v})}>
                  <SelectTrigger><SelectValue placeholder="Opsional" /></SelectTrigger>
                  <SelectContent>{clients.map(c=><SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div><Label>Mulai</Label><Input type="date" value={pForm.start_date||""} onChange={e=>setPForm({...pForm, start_date:e.target.value})}/></div>
              <div><Label>Selesai</Label><Input type="date" value={pForm.end_date||""} onChange={e=>setPForm({...pForm, end_date:e.target.value})}/></div>
              <div><Label>Budget (IDR)</Label><Input type="number" value={pForm.budget} onChange={e=>setPForm({...pForm, budget:e.target.value})}/></div>
              <div><Label>Status</Label>
                <Select value={pForm.status} onValueChange={(v)=>setPForm({...pForm, status:v})}>
                  <SelectTrigger><SelectValue/></SelectTrigger>
                  <SelectContent>{Object.entries(statusMap).map(([k,v])=><SelectItem key={k} value={k}>{v.label}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="col-span-2"><Label>Deskripsi</Label><Textarea value={pForm.description||""} onChange={e=>setPForm({...pForm, description:e.target.value})}/></div>
            </div>
            <DialogFooter><Button data-testid="btn-save-project" onClick={saveProject} className="rounded-sm bg-[#2D4739] hover:bg-[#22362B]">Simpan</Button></DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
        {/* Project list */}
        <div className="lg:col-span-1 space-y-2">
          <div className="label-tag mb-2">Daftar Project</div>
          {projects.length===0 && <div className="card-grid p-4 text-forest-400 text-sm">Belum ada project</div>}
          {projects.map(p => (
            <button key={p.id} onClick={()=>setSelected(p)} data-testid={`project-${p.id}`}
              className={`w-full text-left card-grid p-4 ${selected?.id===p.id?"border-l-4 border-l-[#2D4739]":""}`}>
              <div className="font-medium">{p.name}</div>
              <div className="flex items-center justify-between mt-2">
                <Badge className={`${statusMap[p.status]?.color||"bg-forest-400"} text-white rounded-sm text-[10px]`}>{statusMap[p.status]?.label}</Badge>
                <span className="text-xs num text-forest-400">{formatIDR(p.budget)}</span>
              </div>
            </button>
          ))}
        </div>

        {/* Kanban board */}
        <div className="lg:col-span-3">
          {!selected ? (
            <div className="card-grid p-12 text-center text-forest-400">Pilih atau buat project untuk mulai.</div>
          ) : (
            <>
              <div className="flex items-center justify-between mb-4">
                <div>
                  <h2 className="font-display text-2xl">{selected.name}</h2>
                  <p className="text-xs text-forest-400 mt-1">{formatDate(selected.start_date)} — {formatDate(selected.end_date)} · Budget {formatIDR(selected.budget)}</p>
                </div>
                <div className="flex gap-2">
                  <Dialog open={openT} onOpenChange={(v)=>{setOpenT(v); if(!v)setTForm(emptyT);}}>
                    <DialogTrigger asChild>
                      <Button data-testid="btn-add-task" size="sm" className="rounded-sm bg-[#151A18] hover:bg-[#22362B] gap-1"><Plus size={14}/> Task</Button>
                    </DialogTrigger>
                    <DialogContent className="bg-[#FDFDFB] rounded-sm">
                      <DialogHeader><DialogTitle className="font-display">Task Baru</DialogTitle></DialogHeader>
                      <div className="grid grid-cols-2 gap-3">
                        <div className="col-span-2"><Label>Judul</Label><Input data-testid="inp-task-title" value={tForm.title} onChange={e=>setTForm({...tForm, title:e.target.value})}/></div>
                        <div><Label>PIC</Label><Input value={tForm.assignee||""} onChange={e=>setTForm({...tForm, assignee:e.target.value})}/></div>
                        <div><Label>Deadline</Label><Input type="date" value={tForm.due_date||""} onChange={e=>setTForm({...tForm, due_date:e.target.value})}/></div>
                        <div><Label>Prioritas</Label>
                          <Select value={tForm.priority} onValueChange={(v)=>setTForm({...tForm, priority:v})}>
                            <SelectTrigger><SelectValue/></SelectTrigger>
                            <SelectContent><SelectItem value="low">Rendah</SelectItem><SelectItem value="medium">Sedang</SelectItem><SelectItem value="high">Tinggi</SelectItem></SelectContent>
                          </Select>
                        </div>
                        <div><Label>Status</Label>
                          <Select value={tForm.status} onValueChange={(v)=>setTForm({...tForm, status:v})}>
                            <SelectTrigger><SelectValue/></SelectTrigger>
                            <SelectContent>{Object.entries(taskStatusMap).map(([k,v])=><SelectItem key={k} value={k}>{v}</SelectItem>)}</SelectContent>
                          </Select>
                        </div>
                        <div className="col-span-2"><Label>Deskripsi</Label><Textarea value={tForm.description||""} onChange={e=>setTForm({...tForm, description:e.target.value})}/></div>
                      </div>
                      <DialogFooter><Button data-testid="btn-save-task" onClick={saveTask} className="rounded-sm bg-[#2D4739] hover:bg-[#22362B]">Simpan</Button></DialogFooter>
                    </DialogContent>
                  </Dialog>
                  <Button size="sm" variant="outline" onClick={()=>removeProject(selected.id)} className="rounded-sm text-[#C45A3D]"><Trash2 size={14}/></Button>
                </div>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {columns.map(col => (
                  <div key={col} className="card-grid p-4 min-h-[400px]">
                    <div className="label-tag mb-3 flex items-center justify-between">
                      <span>{taskStatusMap[col]}</span>
                      <span className="text-forest-400">{tasks.filter(t=>t.status===col).length}</span>
                    </div>
                    <div className="space-y-2">
                      {tasks.filter(t=>t.status===col).map(t => (
                        <div key={t.id} className="border border-forest-100 p-3 rounded-sm bg-white">
                          <div className="text-sm font-medium">{t.title}</div>
                          {t.assignee && <div className="text-xs text-forest-400 mt-1">@{t.assignee}</div>}
                          <div className="flex items-center justify-between mt-2">
                            <Badge className={`rounded-sm text-[10px] ${t.priority==="high"?"bg-[#C45A3D]":t.priority==="medium"?"bg-[#D99C3B]":"bg-forest-400"} text-white`}>{t.priority}</Badge>
                            <div className="flex gap-1">
                              <button onClick={()=>moveTask(t, -1)} disabled={col==="todo"} className="p-1 hover:bg-forest-50 disabled:opacity-30"><ChevronLeft size={12}/></button>
                              <button onClick={()=>moveTask(t, 1)} disabled={col==="done"} className="p-1 hover:bg-forest-50 disabled:opacity-30"><ChevronRight size={12}/></button>
                              <button onClick={()=>delTask(t.id)} className="p-1 text-[#C45A3D]"><Trash2 size={12}/></button>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>

              <div className="card-grid p-4 mt-4">
                <AttachmentBox
                  entityType="projects"
                  entityId={selected.id}
                  attachments={selected.attachments || []}
                  onChange={(list) => {
                    setSelected({ ...selected, attachments: list });
                    setProjects(projects.map(p => p.id === selected.id ? { ...p, attachments: list } : p));
                  }}
                />
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
